import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDebouncedState } from '../../hooks/useDebouncedState.ts';
import { ProtocolStatus } from '../../../shared/types/protocol.ts';

export function useProtocolFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchTextParam = searchParams.get('search') ?? '';
    const onlyWithIntervalParam = searchParams.get('onlyWithInterval') === 'true';
    const showDisabledParam = searchParams.get('showDisabled') === 'true';

    const [onlyWithInterval, setOnlyWithInterval] = useState<boolean>(onlyWithIntervalParam);
    const [showDisabled, setShowDisabled] = useState<boolean>(showDisabledParam);
    const [status, setStatus] = useState<ProtocolStatus[] | undefined>(undefined);

    const {
        runningValue: searchTextInput,
        debouncedValue: searchText,
        setValueDebounced: setSearchTextDebounced,
        setValueImmediate: setSearchTextImmediate,
    } = useDebouncedState<string>(searchTextParam, 200, handleSearchTextChanged);

    useEffect(() => {
        setSearchTextImmediate(searchTextParam);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTextParam]);

    function handleSearchTextChanged(text: string) {
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            if (text) newParams.set('search', text);
            else newParams.delete('search');
            return newParams;
        }, { replace: true });
    }

    function handleOnlyWithIntervalChanged(value: boolean) {
        setOnlyWithInterval(value);
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            if (value) newParams.set('onlyWithInterval', 'true');
            else newParams.delete('onlyWithInterval');
            return newParams;
        }, { replace: true });
    }

    function handleShowDisabledChanged(value: boolean) {
        setShowDisabled(value);
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            if (value) newParams.set('showDisabled', 'true');
            else newParams.delete('showDisabled');
            return newParams;
        }, { replace: true });
    }

    function handleClearFilters() {
        setSearchTextImmediate('');
        setOnlyWithInterval(false);
        setShowDisabled(false);
        setSearchParams(new URLSearchParams(), { replace: true });
    }

    return {
        searchTextInput,
        searchText,
        setSearchTextDebounced,
        setSearchTextImmediate,
        onlyWithInterval,
        setOnlyWithInterval: handleOnlyWithIntervalChanged,
        showDisabled,
        setShowDisabled: handleShowDisabledChanged,
        handleClearFilters,
        status,
        setStatus,
    }
}
